import { Brain, MessageCircle, Hand, HeartPulse, Activity, Utensils, Sparkles, ArrowRight, Shield } from "lucide-react";
import { Link } from "react-router-dom";
import { ENAGA_PHOTOS } from "@/lib/localMedia";
import { useConfig } from "@/hooks/useConfig";

const TERAPIAS = [
  { icon: MessageCircle, label: "Fonoaudiologia" },
  { icon: Activity, label: "Fisioterapia" },
  { icon: Brain, label: "Psicologia" },
  { icon: Hand, label: "Terapia ocupacional" },
  { icon: Utensils, label: "Nutrição" },
  { icon: HeartPulse, label: "Atendimento multidisciplinar" },
];

export default function EnagaDestaque() {
  const cfg = useConfig();
  const whatsapp = cfg.whatsapp_url || "https://wa.me/5581999857209";

  return (
    <section
      id="enaga-destaque"
      data-testid="enaga-destaque-section"
      className="section-pad relative overflow-hidden bg-gradient-to-br from-[#0b2a44] via-ga-ink to-[#1a3a6e] text-white"
    >
      <div className="absolute top-20 right-10 w-[28rem] h-[28rem] rounded-full bg-blue-400/20 blur-3xl" />
      <div className="absolute bottom-10 left-10 w-96 h-96 rounded-full bg-ga-amber/15 blur-3xl" />

      <div className="container-ga relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="pill border-blue-200/30 text-blue-200 bg-blue-400/10 mb-6" data-testid="enaga-destaque-pill">
              <Sparkles className="w-3.5 h-3.5" /> Destaque: ENAGA
            </div>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
              Atendimento especializado para{" "}
              <span className="text-ga-amber">crianças com TEA</span>.
            </h2>
            <p className="mt-6 text-white/80 text-lg leading-relaxed">
              A ENAGA é nossa ponte de cuidado em neurodiversidade. Equipe
              multidisciplinar, terapias integradas e ambiente preparado para
              cuidar de cada criança no seu próprio tempo, com respeito, fé e
              estrutura.
            </p>

            <div className="mt-8 grid grid-cols-2 gap-3">
              {TERAPIAS.map((t) => (
                <div
                  key={t.label}
                  data-testid={`enaga-terapia-${t.label.toLowerCase().replace(/\s/g, '-')}`}
                  className="flex items-center gap-3 rounded-2xl bg-white/[0.05] border border-white/10 px-4 py-3"
                >
                  <t.icon className="w-4 h-4 text-ga-amber shrink-0" />
                  <span className="text-sm font-semibold text-white/90">{t.label}</span>
                </div>
              ))}
            </div>

            <a
              href={whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="enaga-destaque-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-ga-amber text-ga-ink font-bold py-3.5 px-7 shadow-lg hover:bg-ga-amber_deep hover:text-white transition-colors"
            >
              Falar com a equipe ENAGA
              <ArrowRight className="w-4 h-4" />
            </a>

            <div className="mt-6 rounded-2xl bg-white/[0.05] border border-white/10 p-4 sm:p-5 flex items-start gap-4" data-testid="enaga-triagem-cta">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-ga-emerald/20 text-ga-emerald flex items-center justify-center">
                <Shield className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <div className="font-display font-bold text-white text-base sm:text-lg leading-tight">
                  Triagem orientativa gratuita
                </div>
                <p className="mt-1 text-sm text-white/70 leading-relaxed">
                  Responda um questionário rápido sobre sinais no desenvolvimento da sua criança e receba uma orientação no seu e-mail.
                </p>
                <Link
                  to="/triagem"
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-ga-emerald hover:text-ga-amber transition-colors"
                  data-testid="enaga-triagem-link"
                >
                  Fazer triagem orientativa
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="relative rounded-3xl overflow-hidden aspect-[3/4] shadow-xl">
                <img
                  src={ENAGA_PHOTOS.autismo}
                  alt="Campanha de conscientização sobre o autismo no Instituto ENAGA"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ga-ink/70 via-transparent to-transparent" />
              </div>
              <div className="space-y-4">
                <div className="relative rounded-3xl overflow-hidden aspect-square shadow-xl">
                  <img
                    src={ENAGA_PHOTOS.atendimento}
                    alt="Atendimento pedagógico individual"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="relative rounded-3xl overflow-hidden aspect-square shadow-xl">
                  <img
                    src={ENAGA_PHOTOS.oficina}
                    alt="Oficina do Instituto ENAGA"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

            <div className="mt-5 rounded-3xl bg-white/[0.06] border border-white/10 p-5 text-sm text-white/85" data-testid="enaga-insight">
              <strong className="text-ga-amber">+48h</strong> é o tempo médio
              entre o primeiro contato e o início do acompanhamento ENAGA.
              Rápido, humano e sempre gratuito para os assistidos.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
